import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import {
  getLead,
  getCrmPlatformSuggestions,
  clearLeadEmailStatus,
  setLeadStatus,
  updateLead,
  deleteLead,
  listAssignees,
  listLeadNotes,
  addLeadNote,
  switchLeadKind,
  sendAffiliateRegistration,
  convertLeadToAffiliate,
  getLeadAffiliate,
  markIntroCall,
  markOfferSent,
  markContractSent,
  getLeadClient,
  type LeadAffiliateLink,
  type LeadDetail,
  type LeadChannel as LeadChannelItem,
  type LeadStatus,
  type LeadKind,
  type SpaceUser,
  type LeadNote,
} from '../api/crm';
import { useAuth } from '../auth/AuthContext';
import { useFeather } from '../shell/useFeather';
import { stagesForSlug, stageDef, type StageDef } from './crm-stages';
import { AP_MODULES, AP_PRODUCTS, FOCUS_OPTIONS, KindBadge } from './CrmLeadyPage';
import { CrmAffiliateFlow } from './CrmAffiliateFlow';
import { CrmClientFlow } from './CrmClientFlow';
import { CrmLeadMails } from './CrmLeadMails';
import { CrmLeadChannels } from './CrmLeadChannels';
import { CrmLeadB2b } from './CrmLeadB2b';
import { b2bOpenUrl, type B2bOutput } from '../api/b2b';
import { B2bVystupyTable } from '../affilmaster/stranky/B2bVystupyTable';
import '../affilmaster/affilmaster-detail.css';
import { COMM_LANGS, INZERENT_SERVICES, INZERENT_TYPES, commLangLabel, inzerentServiceLabels, inzerentTypeLabel } from './crm-lang';
import { draftFromEur, eurFromDraft, czkFirst, money, moneyAlt, moneyUnit } from './crm-money';
import './crm-detail.css';
import './crm-redesign.css';
import { confirmDialog } from '../shell/dialogs';
import { MailStatusDot } from '../mailing/MailStatusDot';

type Section = 'udaje' | 'poznamky' | 'vystupy';

function companyInitials(company: string): string {
  const words = company.trim().split(/\s+/).filter(Boolean);
  return initials(words[0] ?? '', words[1] ?? '');
}

function initials(first: string, last: string) {
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase() || '?';
}
function fmtDateTime(s: string) {
  if (!s) return '-';
  const d = new Date(s);
  return Number.isNaN(d.getTime())
    ? '-'
    : d.toLocaleString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function normalizeUrl(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}
function webHost(url: string | null): string | null {
  if (!url) return null;
  return url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
}

function StatusPicker({
  value,
  disabled,
  stages,
  spaceSlug,
  onChange,
}: {
  value: LeadStatus;
  disabled?: boolean;
  stages: StageDef[];
  spaceSlug: string | null;
  onChange: (s: LeadStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, [open]);
  const cur = stageDef(spaceSlug, value);
  return (
    <div className="crm-statuspick" ref={ref}>
      <button type="button" className="crm-statuspick-trigger" disabled={disabled} onClick={() => setOpen((o) => !o)}>
        <span className="crm-status-dot" style={{ background: cur.color }} />
        <span className="crm-statuspick-label">{cur.label}</span>
        <span className="crm-statuspick-chev">▾</span>
      </button>
      {open && (
        <div className="crm-statuspick-menu" role="listbox">
          {stages.map((s) => (
            <button
              key={s.key}
              type="button"
              role="option"
              aria-selected={s.key === value}
              className={`crm-statuspick-item${s.key === value ? ' active' : ''}`}
              onClick={() => { setOpen(false); onChange(s.key); }}
            >
              <span className="crm-status-dot" style={{ background: s.color }} />
              <span className="crm-statuspick-item-label">{s.label}</span>
              {s.key === value && <span className="crm-statuspick-check">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function CrmLeadDetailPage({
  id,
  onBack,
  onChanged,
  onOpenAffiliate,
  onOpenClient,
}: {
  id: number;
  onBack: () => void;
  onChanged: () => void;
  onOpenAffiliate?: (papAffiliateId: string) => void;
  onOpenClient?: (name: string, partnerId: number) => void;
}) {
  const { me } = useAuth();
  const spaceSlug = me?.activeSpaceSlug ?? null;
  const stages = stagesForSlug(spaceSlug);
  const isAffilmaster = spaceSlug === 'affilmaster';
  const isAffilplatform = spaceSlug === 'affilplatform';
  const hasClientFlow = isAffilmaster || isAffilplatform;

  const [lead, setLead] = useState<LeadDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [section, setSection] = useState<Section>('udaje');

  const [assignees, setAssignees] = useState<SpaceUser[]>([]);
  const [notes, setNotes] = useState<LeadNote[]>([]);
  const [noteInput, setNoteInput] = useState<string>('');
  const [sendingNote, setSendingNote] = useState(false);
  const [revenueDraft, setRevenueDraft] = useState<string>('');
  const [affiliateLink, setAffiliateLink] = useState<LeadAffiliateLink | null>(null);
  const [clientLink, setClientLink] = useState<{ partnerId: number; name: string } | null>(null);
  const [platformSuggestions, setPlatformSuggestions] = useState<{ platforms: string[]; affiliatePlatforms: string[] }>({ platforms: [], affiliatePlatforms: [] });

  useEffect(() => {
    if (!lead || (isAffilmaster && lead.kind !== 'inzerent')) return;
    let cancelled = false;
    getCrmPlatformSuggestions()
      .then((s) => { if (!cancelled) setPlatformSuggestions(s); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [isAffilmaster, lead?.id, lead?.kind, lead?.platform, lead?.affiliatePlatform]);

  useEffect(() => {
    let cancelled = false;
    getLead(id)
      .then((dd) => {
        if (cancelled) return;
        setLead(dd);
        setRevenueDraft(draftFromEur(dd.expectedMonthlyEur, spaceSlug));
        setError(null);
      })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Detail selhal'); });
    listAssignees()
      .then((u) => { if (!cancelled) setAssignees(u); })
      .catch(() => { if (!cancelled) setAssignees([]); });
    listLeadNotes(id)
      .then((n) => { if (!cancelled) setNotes(n); })
      .catch(() => { if (!cancelled) setNotes([]); });
    return () => { cancelled = true; };
  }, [id]);

  const convertedAt = lead?.convertedAt ?? null;
  useEffect(() => {
    let cancelled = false;
    if (!isAffilmaster || !convertedAt) { setAffiliateLink(null); return; }
    getLeadAffiliate(id)
      .then((link) => { if (!cancelled) setAffiliateLink(link); })
      .catch(() => { if (!cancelled) setAffiliateLink(null); });
    return () => { cancelled = true; };
  }, [id, isAffilmaster, convertedAt]);

  const clientPartnerId = lead?.clientPartnerId ?? null;
  useEffect(() => {
    let cancelled = false;
    if (!hasClientFlow || !clientPartnerId) { setClientLink(null); return; }
    getLeadClient(id)
      .then((c) => { if (!cancelled) setClientLink(c); })
      .catch(() => { if (!cancelled) setClientLink(null); });
    return () => { cancelled = true; };
  }, [id, hasClientFlow, clientPartnerId]);

  function introCall() {
    setBusy(true);
    setError(null);
    setInfo(null);
    markIntroCall(id)
      .then(() => reloadLead())
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Uložení selhalo'))
      .finally(() => setBusy(false));
  }

  function offerSent(url: string | null) {
    setBusy(true);
    setError(null);
    setInfo(null);
    markOfferSent(id, url)
      .then(() => reloadLead())
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Uložení selhalo'))
      .finally(() => setBusy(false));
  }

  function contractSent() {
    setBusy(true);
    setError(null);
    setInfo(null);
    markContractSent(id)
      .then(() => reloadLead())
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Uložení selhalo'))
      .finally(() => setBusy(false));
  }

  const [b2bOutputs, setB2bOutputs] = useState<B2bOutput[]>([]);
  const onB2bLinked = useCallback((outputs: B2bOutput[]) => setB2bOutputs(outputs), []);

  function reloadChannels(next: LeadChannelItem[]) {
    setLead((l) => (l ? { ...l, channels: next } : l));
    getLead(id).then((fresh) => setLead(fresh)).catch(() => undefined);
  }

  function reloadLead() {
    return getLead(id).then((fresh) => { setLead(fresh); onChanged(); });
  }

  async function sendRegistration() {
    if (!lead?.email) return;
    const again = !!lead.registrationSentAt;
    const msg = again
      ? `Poslat registraci do affiliate platformy znovu na ${lead.email}?`
      : `Schválit zájemce a poslat mu registraci do affiliate platformy na ${lead.email}?`;
    if (!(await confirmDialog({ title: again ? 'Poslat registraci znovu?' : 'Schválit zájemce?', message: msg, confirmLabel: again ? 'Poslat znovu' : 'Schválit a poslat' }))) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    sendAffiliateRegistration(id)
      .then(() => reloadLead())
      .then(() => setInfo(`Registrace odeslána na ${lead.email}.`))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Registraci se nepodařilo odeslat'))
      .finally(() => setBusy(false));
  }

  async function convertToAffiliate(market: 'CZ' | 'SK' | null) {
    if (!lead) return;
    const who = `${lead.firstName} ${lead.lastName}`.trim();
    if (!(await confirmDialog({ title: `Získat ${who} jako affiliate partnera?`, message: 'Založí se karta v Affiliate partnerech a lead se uzavře jako úspěšný. Potom na kartě partnera doplňte PaP ID.', confirmLabel: 'Získat' }))) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    convertLeadToAffiliate(id, market)
      .then(() => reloadLead())
      .then(() => setInfo('Affiliate partner založen, lead uzavřen jako úspěšný. Doplňte na kartě partnera PaP ID.'))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Získání affiliate selhalo'))
      .finally(() => setBusy(false));
  }

  async function rejectLead() {
    if (!lead) return;
    const who = `${lead.firstName} ${lead.lastName}`.trim() || 'tento lead';
    if (!(await confirmDialog({ title: `Neschválit ${who}?`, message: 'Lead se uzavře jako neschválený (spam nebo nevyhovující zájemce). Kdykoli ho můžete vrátit zpět.', confirmLabel: 'Neschválit', danger: true }))) return;
    changeStatus('rejected');
  }

  function reopenLead() {
    if (!lead) return;
    const started = !!lead.registrationSentAt || !!lead.introCallAt || !!lead.offerSentAt;
    changeStatus(started ? 'contacted' : 'new');
  }

  function changeStatus(next: LeadStatus) {
    if (!lead || next === lead.status) return;
    setBusy(true);
    setLeadStatus(id, next)
      .then(() => { setLead((l) => (l ? { ...l, status: next } : l)); onChanged(); })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Změna stavu selhala'))
      .finally(() => setBusy(false));
  }

  async function convertLead(target: LeadKind) {
    if (!lead) return;
    const who = `${lead.firstName} ${lead.lastName}`.trim();
    const pozvankaOnly = isAffilmaster && (lead.services ?? []).length > 0 && (lead.services ?? []).every((s) => s === 'pozvanka');
    const msg = target === 'inzerent' && pozvankaOnly
      ? `Zapsat Pozvánku pro ${lead.company?.trim() || who} jako deal?\n\nPoužijte až po podpisu smlouvy. Lead se připojí ke klientovi se stejným IČ (když takový klient není, založí se) a na jeho kartě vznikne deal Pozvánka ve stavu Připraveno. Lead se uzavře jako úspěšný.`
      : target === 'inzerent'
      ? `Získat ${who} jako klienta?\n\nPoužijte až po podpisu smlouvy. Založí se partner v Klientech (společnost, IČ, DIČ, web) a kontakt (jméno, e-mail, telefon). Lead se uzavře jako úspěšný.`
      : `Získat ${who} jako affiliate partnera?${lead.email ? '\n\nOdešle se uvítací e-mail na ' + lead.email + ' a lead se uzavře jako úspěšný.' : '\n\nLead se uzavře jako úspěšný. Uvítací e-mail nelze odeslat, lead nemá e-mail.'}`;
    const [title, ...rest] = msg.split('\n\n');
    if (!(await confirmDialog({ title: title ?? msg, message: rest.join('\n\n'), confirmLabel: 'Potvrdit' }))) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    switchLeadKind(id, target)
      .then(async (res) => {
        await reloadLead();
        if (target === 'inzerent' && res.dealId) {
          setInfo(res.dealCreated
            ? `Deal Pozvánka (${new Intl.NumberFormat('cs-CZ').format(res.dealAmount ?? 0)} ${res.dealCurrency === 'EUR' ? '€' : 'Kč'} měsíčně) je založený na kartě klienta jako připravený. Aktivujte ho tam, až se Pozvánka zapne.`
            : 'Klient už deal Pozvánka má, nový jsme nezakládali. Lead je připojený ke klientovi a uzavřený jako úspěšný.');
        } else if (target === 'inzerent') {
          setInfo(res.partnerCreated
            ? 'Klient je založený v Klientech a lead je uzavřený jako úspěšný.'
            : 'Klient se stejným IČ už v Klientech je, lead jsme k němu připojili a uzavřeli jako úspěšný.');
        } else {
          setInfo('Affiliate partner získán. Lead uzavřen jako úspěšný.');
        }
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Konverze selhala'))
      .finally(() => setBusy(false));
  }

  function changeAssignee(value: string) {
    const uid = value === '' ? null : Number(value);
    setBusy(true);
    updateLead(id, { assignedToUserId: uid })
      .then(() => {
        const name = uid == null ? null : assignees.find((u) => u.id === uid)?.name ?? null;
        setLead((l) => (l ? { ...l, assignedToUserId: uid, assignedToName: name } : l));
        onChanged();
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Přiřazení selhalo'))
      .finally(() => setBusy(false));
  }

  function saveRevenue() {
    if (!lead) return;
    const trimmed = revenueDraft.trim().replace(',', '.');
    const next = trimmed === '' ? null : eurFromDraft(Number(trimmed), spaceSlug);
    if (trimmed !== '' && (!Number.isFinite(next as number) || (next as number) < 0)) {
      setRevenueDraft(draftFromEur(lead.expectedMonthlyEur, spaceSlug));
      return;
    }
    if (next === lead.expectedMonthlyEur) return;
    setBusy(true);
    updateLead(id, { expectedMonthlyEur: next })
      .then(() => { setLead((l) => (l ? { ...l, expectedMonthlyEur: next } : l)); onChanged(); })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Uložení příjmu selhalo'))
      .finally(() => setBusy(false));
  }

  function saveField(patch: Parameters<typeof updateLead>[1]) {
    setBusy(true);
    setError(null);
    updateLead(id, patch)
      .then(async () => {
        const fresh = 'firstName' in patch && !('vocative' in patch) ? await getLead(id).catch(() => null) : null;
        setLead((l) => (fresh ?? (l ? { ...l, ...patch } as LeadDetail : l)));
        onChanged();
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Uložení selhalo'))
      .finally(() => setBusy(false));
  }

  async function removeLead() {
    if (!lead) return;
    if (!(await confirmDialog({ title: 'Odstranit lead?', message: `${lead.firstName} ${lead.lastName} zmizí ze seznamu i pipeline.`, confirmLabel: 'Odstranit', danger: true }))) return;
    setBusy(true);
    deleteLead(id)
      .then(() => { onChanged(); onBack(); })
      .catch((e: unknown) => { setError(e instanceof ApiError ? e.message : 'Odstranění selhalo'); setBusy(false); });
  }

  function sendNote() {
    const text = noteInput.trim();
    if (!text || sendingNote) return;
    setSendingNote(true);
    addLeadNote(id, text)
      .then((n) => { setNotes((ns) => [...ns, n]); setNoteInput(''); onChanged(); })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Poznámku se nepodařilo uložit'))
      .finally(() => setSendingNote(false));
  }

  useFeather([lead, section]);

  const d = lead;
  const host = webHost(d?.website ?? null);

  return (
    <div className="klient-detail-view crm-lead-detail crm-page">
      <div className="mb-3 d-flex align-items-center gap-2">
        <button type="button" className="btn btn-sm btn-link text-muted p-0 aff-detail-back" onClick={onBack}>Zpět na seznam</button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {info && <div className="alert alert-success">{info}</div>}

      {!d && !error && <div className="text-center py-5 text-muted"><span className="spinner-border spinner-border-sm me-2" /> Načítám…</div>}

      {d && (
        <>
          <section className="crm-opportunity-hero">
            <div className="crm-opportunity-identity">
              <div className="crm-lh-top">
                <div className="aff-avatar">{!isAffilmaster && d.company ? companyInitials(d.company) : initials(d.firstName, d.lastName)}</div>
                <div className="crm-lh-idwrap">
                  <span className="crm-detail-eyebrow">Obchodní příležitost</span>
                  <div className="crm-lh-name">
                    <span>{!isAffilmaster && d.company ? d.company : `${d.firstName} ${d.lastName}`}</span>
                    {isAffilmaster && <KindBadge kind={d.kind} />}
                  </div>
                  <div className="crm-opportunity-contact">
                    {isAffilmaster
                      ? (d.company && <strong>{d.company}</strong>)
                      : <strong>{d.firstName} {d.lastName}</strong>}
                    {!isAffilmaster && d.brand ? <span className="crm-lh-meta"><small>Brand</small>{d.brand}</span> : null}
                    {d.extendsPartnerId ? <span className="crm-lh-meta crm-lh-extends"><small>Rozšíření klienta</small>{onOpenClient ? <button type="button" onClick={() => onOpenClient(d.extendsPartnerName ?? d.company ?? '', d.extendsPartnerId!)}>{d.extendsPartnerName ?? `#${d.extendsPartnerId}`}</button> : (d.extendsPartnerName ?? `#${d.extendsPartnerId}`)}</span> : null}
                    {isAffilmaster && d.kind === 'inzerent'
                      ? (d.product && <span className="crm-lh-meta"><small>Typ</small>{inzerentTypeLabel(d.product)}</span>)
                      : (d.focus && <span className="crm-lh-meta"><small>Zaměření</small>{d.focus}</span>)}
                    {isAffilmaster && d.kind === 'inzerent' && d.services?.length ? <span className="crm-lh-meta"><small>Produkt</small>{inzerentServiceLabels(d.services).join(', ')}</span> : null}
                    {isAffilmaster && d.kind === 'inzerent' && d.platform ? <span className="crm-lh-meta"><small>Kasino platforma</small>{d.platform}</span> : null}
                    {isAffilmaster && d.kind === 'inzerent' && d.affiliatePlatform ? <span className="crm-lh-meta"><small>Affiliate platforma</small>{d.affiliatePlatform}</span> : null}
                    {d.lang ? <span className="crm-lh-meta"><small>Jazyk</small>{commLangLabel(d.lang)}</span> : null}
                    {!(isAffilmaster && d.kind === 'inzerent') && d.product ? <span className="crm-lh-meta"><small>Produkt</small>{d.product}</span> : null}
                    {!(isAffilmaster && d.kind === 'inzerent') && d.module ? <span className="crm-lh-meta"><small>Modul</small>{d.module}</span> : null}
                    {d.email && d.emailStatus && <MailStatusDot status={d.emailStatus} onClear={async () => { await clearLeadEmailStatus(d.id); await reloadLead(); }} />}
                    {d.email && <a href={`mailto:${d.email}`}>{d.email}</a>}
                    {d.phone && <a href={`tel:${d.phone}`}>{d.phone}</a>}
                    {host && <a href={normalizeUrl(d.website!)} target="_blank" rel="noreferrer">{host}</a>}
                    {!host && isAffilmaster && d.kind === 'affiliate' && (d.channels ?? []).slice(0, 3).map((ch) => (
                      <a key={ch.id} href={ch.url} target="_blank" rel="noreferrer">{ch.host}</a>
                    ))}
                  </div>
                </div>
              </div>
              <p className="crm-opportunity-hint">Udržujte další obchodní krok, odpovědnost a očekávanou hodnotu pohromadě.</p>
            </div>

            <div className="crm-opportunity-sidebar" aria-label="Řízení příležitosti">
                <div className="crm-command-field">
                  <label>Fáze</label>
                  <StatusPicker value={d.status} disabled={busy} stages={stages} spaceSlug={spaceSlug} onChange={changeStatus} />
                </div>
                {isAffilmaster && d.kind === 'inzerent' && (
                <div className="crm-command-field crm-command-services">
                  <label>Produkt</label>
                  <div className="udaje-checks">
                    {INZERENT_SERVICES.map((o) => {
                      const on = (d.services ?? []).includes(o.value);
                      return (
                        <button
                          key={o.value}
                          type="button"
                          className={`udaje-check${on ? ' is-on' : ''}`}
                          aria-pressed={on}
                          disabled={busy}
                          onClick={() => saveField({ services: on ? (d.services ?? []).filter((v) => v !== o.value) : [...(d.services ?? []), o.value] })}
                        >
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                  {(d.services ?? []).length === 0 && <div className="crm-command-hint">Vyberte aspoň jeden produkt.</div>}
                </div>
                )}
                <div className="crm-command-field">
                  <label>Vlastník</label>
                  <select className="form-select form-select-sm" value={d.assignedToUserId != null ? String(d.assignedToUserId) : ''} disabled={busy} onChange={(e) => changeAssignee(e.target.value)}>
                    <option value="">- nepřiřazeno -</option>
                    {assignees.map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
                  </select>
                </div>
                {!(isAffilmaster && d.kind === 'affiliate') && (
                <div className="crm-command-field crm-command-value">
                  <label>Očekávaná hodnota</label>
                  <div className="input-group input-group-sm">
                    <input
                      type="number"
                      min="0"
                      step={czkFirst(spaceSlug) ? '500' : '50'}
                      inputMode="decimal"
                      className="form-control form-control-sm"
                      placeholder="0"
                      value={revenueDraft}
                      disabled={busy}
                      onChange={(e) => setRevenueDraft(e.target.value)}
                      onBlur={saveRevenue}
                      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                    />
                    <span className="input-group-text">{moneyUnit(spaceSlug)} / měs</span>
                  </div>
                  <div className="crm-hero-annual">
                    {d.expectedMonthlyEur
                      ? `${moneyAlt(d.expectedMonthlyEur, spaceSlug) ? `${moneyAlt(d.expectedMonthlyEur, spaceSlug)} / měs · ` : ''}≈ ${money(d.expectedMonthlyEur * 12, spaceSlug)} / rok`
                      : 'ročně se dopočítá'}
                  </div>
                </div>
                )}
            </div>
          </section>

          <CrmLeadMails lead={d} showRegistration={isAffilmaster && d.kind === 'affiliate'} />

          {((isAffilmaster && d.kind === 'inzerent') || isAffilplatform) && (
            <CrmClientFlow
              lead={d}
              client={clientLink}
              busy={busy}
              onIntroCall={introCall}
              onOfferSent={offerSent}
              onContractSent={contractSent}
              onConvert={() => convertLead('inzerent')}
              onOpenClient={onOpenClient}
              onReject={rejectLead}
              onReopen={reopenLead}
              requireProduct={isAffilmaster && d.kind === 'inzerent'}
              offerUrlFallback={isAffilmaster && b2bOutputs[0] ? b2bOpenUrl(b2bOutputs[0]) : null}
            />
          )}

          {isAffilmaster && d.kind === 'affiliate' && (
            <CrmAffiliateFlow
              lead={d}
              affiliate={affiliateLink}
              busy={busy}
              onSendRegistration={sendRegistration}
              onConvert={convertToAffiliate}
              onOpenAffiliate={onOpenAffiliate}
              onReject={rejectLead}
              onReopen={reopenLead}
            />
          )}

          <ul className="nav nav-pills nav-pills-flush mb-2" role="tablist">
            <li className="nav-item" role="presentation">
              <button type="button" className={`nav-link ${section === 'udaje' ? 'active' : ''}`} onClick={() => setSection('udaje')}>Údaje</button>
            </li>
            <li className="nav-item" role="presentation">
              <button type="button" className={`nav-link ${section === 'poznamky' ? 'active' : ''}`} onClick={() => setSection('poznamky')}>
                Poznámky{notes.length > 0 ? <span className="cnt">{notes.length}</span> : null}
              </button>
            </li>
            {isAffilmaster && d.kind === 'inzerent' && (
              <li className="nav-item" role="presentation">
                <button type="button" className={`nav-link ${section === 'vystupy' ? 'active' : ''}`} onClick={() => setSection('vystupy')}>Výstupy</button>
              </li>
            )}
          </ul>

          <div className="card shadow-sm">
            <div className="card-body">
              {section === 'udaje' && (
                <div className="udaje-cols">
                  <div>
                    <div className="udaje-section">
                      <div className="udaje-section-head">Kontakt</div>
                      <div className="udaje-card">
                        <UdajeEdit label="Jméno" value={d.firstName} disabled={busy} onSave={(v) => saveField({ firstName: v })} />
                        <UdajeEdit label="Příjmení" value={d.lastName} disabled={busy} onSave={(v) => saveField({ lastName: v })} />
                        <UdajeEdit label="Oslovení" value={d.vocative} disabled={busy} placeholder="bez oslovení" onSave={(v) => saveField({ vocative: v.trim() })} />
                        <UdajeSelect label="Jazyk komunikace" value={d.lang ?? ''} options={COMM_LANGS.map((l) => ({ value: l.code, label: l.label }))} disabled={busy} onSave={(v) => saveField({ lang: v || null })} />
                        <UdajeEdit label="E-mail" value={d.email} disabled={busy} onSave={(v) => saveField({ email: v || null })} />
                        <UdajeEdit label="Telefon" value={d.phone} disabled={busy} onSave={(v) => saveField({ phone: v || null })} />
                        <UdajeEdit label="Společnost" value={d.company} disabled={busy} onSave={(v) => saveField({ company: v || null })} />
                        {isAffilmaster && d.kind === 'affiliate'
                          ? <UdajeEdit label="Zaměření" value={d.focus} disabled={busy} onSave={(v) => saveField({ focus: v || null })} />
                          : <UdajeEdit label="Web" value={d.website} disabled={busy} placeholder="https://" onSave={(v) => saveField({ website: v || null })} />}
                      </div>
                    </div>
                  </div>
                  <div>
                    {isAffilmaster && d.kind === 'inzerent' && (
                      <CrmLeadB2b leadId={d.id} company={d.company} canEdit={!busy} onLinked={onB2bLinked} />
                    )}
                    {isAffilmaster && d.kind === 'affiliate' && (
                      <CrmLeadChannels
                        leadId={d.id}
                        channels={d.channels ?? []}
                        canEdit={!busy}
                        onChanged={reloadChannels}
                      />
                    )}
                    {!(isAffilmaster && d.kind === 'affiliate') && (
                    <div className="udaje-section">
                      <div className="udaje-section-head">{isAffilmaster && d.kind === 'inzerent' ? 'Fakturační údaje' : 'Poptávka'}</div>
                      <div className="udaje-card">
                        {isAffilmaster && d.kind === 'inzerent' ? (
                          <>
                            <UdajeSelect label="Typ" value={d.product} options={INZERENT_TYPES} disabled={busy} onSave={(v) => saveField({ product: v || null })} />
                            <UdajeChecks label="Produkt" values={d.services ?? []} options={INZERENT_SERVICES} disabled={busy} onSave={(v) => saveField({ services: v })} />
                            <UdajeEdit label="Kasino platforma" value={d.platform} placeholder="např. SoftSwiss, EveryMatrix, vlastní" suggestions={platformSuggestions.platforms} disabled={busy} onSave={(v) => saveField({ platform: v || null })} />
                            <UdajeEdit label="Affiliate platforma" value={d.affiliatePlatform} placeholder="např. Post Affiliate Pro, Scaleo, žádná" suggestions={platformSuggestions.affiliatePlatforms} disabled={busy} onSave={(v) => saveField({ affiliatePlatform: v || null })} />
                            <UdajeEdit label="IČ" value={d.ic} disabled={busy} onSave={(v) => saveField({ ic: v || null })} />
                            <UdajeEdit label="DIČ" value={d.dic} disabled={busy} onSave={(v) => saveField({ dic: v || null })} />
                          </>
                        ) : (
                          <>
                            <UdajeEdit label="Název brandu" value={d.brand} disabled={busy} onSave={(v) => saveField({ brand: v || null })} />
                            <UdajeSelect label="Zaměření" value={d.focus} options={withCurrent(FOCUS_OPTIONS, d.focus)} disabled={busy} onSave={(v) => saveField({ focus: v || null })} />
                            <UdajeSelect label="Produkt" value={d.product} options={withCurrent(AP_PRODUCTS, d.product)} disabled={busy} onSave={(v) => saveField({ product: v || null })} />
                            <UdajeSelect label="Modul" value={d.module} options={withCurrent(AP_MODULES, d.module)} disabled={busy} onSave={(v) => saveField({ module: v || null })} />
                            <UdajeEdit label="Kasino platforma" value={d.platform} placeholder="např. SoftSwiss, EveryMatrix, vlastní" suggestions={platformSuggestions.platforms} disabled={busy} onSave={(v) => saveField({ platform: v || null })} />
                            <UdajeEdit label="Affiliate platforma" value={d.affiliatePlatform} placeholder="např. Post Affiliate Pro, Scaleo, žádná" suggestions={platformSuggestions.affiliatePlatforms} disabled={busy} onSave={(v) => saveField({ affiliatePlatform: v || null })} />
                          </>
                        )}
                      </div>
                    </div>
                    )}
                    <div className="udaje-section">
                      <div className="udaje-section-head">Původ</div>
                      <div className="udaje-card">
                        <UdajeItem label="Zdroj (web)" value={d.sourceHost} />
                        <UdajeItem label="Odkud přišel" value={webHost(d.sourceUrl)} link={d.sourceUrl ? normalizeUrl(d.sourceUrl) : null} />
                        <UdajeItem label="IP adresa" value={d.sourceIp} />
                        <UdajeItem label="Přišel" value={fmtDateTime(d.createdAt)} />
                        <UdajeItem label="Poslední změna" value={fmtDateTime(d.updatedAt)} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {section === 'vystupy' && isAffilmaster && d.kind === 'inzerent' && (
                <B2bVystupyTable leadId={d.id} showKind showClient={false} />
              )}

              {section === 'poznamky' && (
                <div className="crm-notes">
                  {notes.length === 0 ? (
                    <div className="crm-note-empty">Zatím prázdné.</div>
                  ) : (
                    <div className="crm-notes-feed">
                      {notes.map((n) => (
                        <div key={n.id} className="crm-note">
                          <div className="crm-note-meta">
                            <span className="crm-note-author">{n.authorName ?? 'Uživatel'}</span>
                            <span className="crm-note-time">{fmtDateTime(n.createdAt)}</span>
                          </div>
                          <div className="crm-note-body">{n.body}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="crm-note-compose">
                    <textarea
                      className="form-control"
                      rows={1}
                      value={noteInput}
                      placeholder="Napsat poznámku…"
                      disabled={sendingNote}
                      onChange={(e) => setNoteInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendNote(); } }}
                    />
                    <button type="button" className="btn btn-primary" disabled={sendingNote || !noteInput.trim()} onClick={sendNote}>
                      {sendingNote ? '…' : 'Odeslat'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="card shadow-sm mt-3 crm-lead-danger">
            <div className="card-body">
              <div className="crm-danger-head">Odstranit lead</div>
              <div className="crm-danger-desc">
                Lead {d.firstName} {d.lastName} bude smazán ze seznamu i pipeline. Poznámky a historie zůstanou zachované v databázi.
              </div>
              <button type="button" className="btn btn-sm btn-outline-danger" disabled={busy} onClick={removeLead}>
                Odstranit lead
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function UdajeEdit({
  label,
  value,
  disabled,
  placeholder,
  suggestions,
  onSave,
}: {
  label: string;
  value?: string | null;
  disabled?: boolean;
  placeholder?: string;
  suggestions?: string[];
  onSave: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value ?? '');
  const [focused, setFocused] = useState(false);
  const listId = useId();
  useEffect(() => { if (!focused) setDraft(value ?? ''); }, [value, focused]);
  return (
    <div className="udaje-item">
      <div className="udaje-item-label">{label}</div>
      <div className="udaje-item-value">
        {suggestions && suggestions.length > 0 && (
          <datalist id={listId}>{suggestions.map((s) => <option key={s} value={s} />)}</datalist>
        )}
        <input
          className="udaje-input"
          list={suggestions && suggestions.length > 0 ? listId : undefined}
          value={draft}
          disabled={disabled}
          placeholder={placeholder ?? '-'}
          onFocus={() => setFocused(true)}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => { setFocused(false); if (draft !== (value ?? '')) onSave(draft); }}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        />
      </div>
    </div>
  );
}

function withCurrent(options: readonly string[], current: string | null | undefined): { value: string; label: string }[] {
  const list = current && !options.includes(current) ? [...options, current] : [...options];
  return list.map((o) => ({ value: o, label: o }));
}

function UdajeSelect({
  label,
  value,
  options,
  disabled,
  onSave,
}: {
  label: string;
  value?: string | null;
  options: { value: string; label: string }[];
  disabled?: boolean;
  onSave: (next: string) => void;
}) {
  return (
    <div className="udaje-item">
      <div className="udaje-item-label">{label}</div>
      <div className="udaje-item-value">
        <select className="udaje-input udaje-select" value={value ?? ''} disabled={disabled} onChange={(e) => onSave(e.target.value)}>
          <option value="">-</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    </div>
  );
}

function UdajeChecks({
  label,
  values,
  options,
  disabled,
  onSave,
}: {
  label: string;
  values: string[];
  options: { value: string; label: string }[];
  disabled?: boolean;
  onSave: (next: string[]) => void;
}) {
  return (
    <div className="udaje-item">
      <div className="udaje-item-label">{label}</div>
      <div className="udaje-item-value udaje-checks">
        {options.map((o) => {
          const on = values.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              className={`udaje-check${on ? ' is-on' : ''}`}
              aria-pressed={on}
              disabled={disabled}
              onClick={() => onSave(on ? values.filter((v) => v !== o.value) : [...values, o.value])}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function UdajeItem({ label, value, link }: { label: string; value?: string | null; link?: string | null }) {
  const has = value != null && value !== '';
  const inner: ReactNode = !has
    ? <span className="text-muted">-</span>
    : link
      ? <a href={link} target={link.startsWith('mailto') || link.startsWith('tel') ? undefined : '_blank'} rel="noreferrer">{value}</a>
      : value;
  return (
    <div className="udaje-item">
      <div className="udaje-item-label">{label}</div>
      <div className="udaje-item-value">{inner}</div>
    </div>
  );
}
